import { useState, useCallback } from 'react';
import { useDisclosure, useToast } from '@chakra-ui/react';

import { apiCreateQuoteConsent } from 'api/quote-marketplace';
import { RECOMMENDED_HABITATIONAL_LIABILITY_LIMIT } from '../constants/habitationalCoveragesOptions';
import { getUnderwritingApprovalThreshold } from '../constants/provinceCoverageAvailability';
import {
  CONSENT_TYPE_HAB_LIABILITY_LIMIT_REDUCTION,
  CONSENT_TYPE_HAB_LIABILITY_UNDERWRITING_APPROVAL,
} from '../constants';

interface PendingChange {
  newValue: number;
  currentValue: number | null;
}

interface UseHabitationalLiabilityLimitConfirmationOptions {
  quoteId: number;
  province: number;
  currentLiabilityLimit: number | null;
  onApplyChange: (newValue: number | null) => void;
}

export const useHabitationalLiabilityLimitConfirmation = ({
  quoteId,
  province,
  currentLiabilityLimit,
  onApplyChange,
}: UseHabitationalLiabilityLimitConfirmationOptions) => {
  const toast = useToast();

  const {
    isOpen: isWarningOpen,
    onOpen: onWarningOpen,
    onClose: onWarningClose,
  } = useDisclosure();

  const {
    isOpen: isUnderwritingOpen,
    onOpen: onUnderwritingOpen,
    onClose: onUnderwritingClose,
  } = useDisclosure();

  const [pendingChange, setPendingChange] = useState<PendingChange | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [needsUnderwritingApproval, setNeedsUnderwritingApproval] =
    useState(false);

  const underwritingThreshold = getUnderwritingApprovalThreshold(province);

  const resetState = useCallback(() => {
    setPendingChange(null);
    setNeedsUnderwritingApproval(false);
  }, []);

  const handleLiabilityLimitChange = useCallback(
    (newValue: number | null) => {
      if (newValue === null) {
        onApplyChange(null);
        return;
      }

      const currentValue = currentLiabilityLimit;
      const isCurrentAtOrAboveRecommended =
        currentValue === null ||
        currentValue >= RECOMMENDED_HABITATIONAL_LIABILITY_LIMIT;
      const isNewBelowRecommended =
        newValue < RECOMMENDED_HABITATIONAL_LIABILITY_LIMIT;
      const isNewBelowUnderwritingThreshold = newValue < underwritingThreshold;

      if (isCurrentAtOrAboveRecommended && isNewBelowRecommended) {
        setPendingChange({ newValue, currentValue });
        setNeedsUnderwritingApproval(isNewBelowUnderwritingThreshold);
        onWarningOpen();
      } else if (
        isNewBelowUnderwritingThreshold &&
        currentValue !== null &&
        currentValue >= underwritingThreshold
      ) {
        setPendingChange({ newValue, currentValue });
        setNeedsUnderwritingApproval(true);
        onUnderwritingOpen();
      } else {
        onApplyChange(newValue);
      }
    },
    [
      currentLiabilityLimit,
      underwritingThreshold,
      onApplyChange,
      onWarningOpen,
      onUnderwritingOpen,
    ],
  );

  const handleWarningConfirm = useCallback(
    async (consentMethod: number) => {
      if (pendingChange === null) return;

      setIsLoading(true);
      try {
        await apiCreateQuoteConsent(quoteId, {
          type: CONSENT_TYPE_HAB_LIABILITY_LIMIT_REDUCTION,
          method: consentMethod,
        });

        onWarningClose();

        if (needsUnderwritingApproval) {
          onUnderwritingOpen();
        } else {
          onApplyChange(pendingChange.newValue);
          setPendingChange(null);
        }
      } catch {
        toast({
          title: 'Error',
          description: 'Failed to record consent. Please try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setIsLoading(false);
      }
    },
    [
      pendingChange,
      quoteId,
      needsUnderwritingApproval,
      onApplyChange,
      onWarningClose,
      onUnderwritingOpen,
      toast,
    ],
  );

  const handleUnderwritingConfirm = useCallback(
    async (consentMethod: number) => {
      if (pendingChange === null) return;

      setIsLoading(true);
      try {
        await apiCreateQuoteConsent(quoteId, {
          type: CONSENT_TYPE_HAB_LIABILITY_UNDERWRITING_APPROVAL,
          method: consentMethod,
        });

        onApplyChange(pendingChange.newValue);
        onUnderwritingClose();
        setPendingChange(null);
        setNeedsUnderwritingApproval(false);
      } catch {
        toast({
          title: 'Error',
          description: 'Failed to record consent. Please try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setIsLoading(false);
      }
    },
    [pendingChange, quoteId, onApplyChange, onUnderwritingClose, toast],
  );

  const handleWarningCancel = useCallback(() => {
    setPendingChange(null);
    setNeedsUnderwritingApproval(false);
    onWarningClose();
  }, [onWarningClose]);

  const handleUnderwritingCancel = useCallback(() => {
    setPendingChange(null);
    setNeedsUnderwritingApproval(false);
    onUnderwritingClose();
  }, [onUnderwritingClose]);

  const formatCurrency = (value: number | null): string => {
    if (value === null) return '$2,000,000';
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  return {
    handleLiabilityLimitChange,
    resetState,
    warningModalProps: {
      isOpen: isWarningOpen,
      onClose: handleWarningCancel,
      onConfirm: handleWarningConfirm,
      isLoading,
      currentLimit: formatCurrency(
        pendingChange?.currentValue ?? currentLiabilityLimit,
      ),
      newLimit: formatCurrency(pendingChange?.newValue ?? null),
    },
    underwritingModalProps: {
      isOpen: isUnderwritingOpen,
      onClose: handleUnderwritingCancel,
      onConfirm: handleUnderwritingConfirm,
      isLoading,
      newLimit: formatCurrency(pendingChange?.newValue ?? null),
      threshold: formatCurrency(underwritingThreshold),
    },
  };
};
